/**
 * 管理画面アクセス権チェック（admin / educator / student）
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { AuthService } from '../../lib/auth';
import { Card, CardContent } from '../ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

interface AdminGuardProps {
  children: React.ReactNode;
}

const STAFF_ROLES = ['admin', 'educator', 'student'] as const;

export default function AdminGuard({ children }: AdminGuardProps) {
  const [loading, setLoading] = useState(true);
  const [canAccess, setCanAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const authService = AuthService.getInstance();

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const authStatus = await authService.getAuthStatus();

        if (!authStatus.isAuthenticated) {
          setLocation('/login');
          return;
        }

        try {
          const user = await authService.getCurrentUser();
          if (!user) {
            setError('認証が必要です。ログインしてください。');
            setLocation('/login');
            return;
          }
          const role = user.role;
          if (!role || !STAFF_ROLES.includes(role)) {
            setError('管理画面にアクセスする権限がありません。');
            setCanAccess(false);
            return;
          }
          setCanAccess(true);
        } catch (err) {
          console.error('管理画面アクセス権チェックエラー:', err);
          setError('権限の確認中にエラーが発生しました');
        }
      } catch (err) {
        console.error('認証チェックエラー:', err);
        setError('認証状態の確認に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [authService, setLocation]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-medical-blue mb-4" />
            <p className="text-gray-600">アクセス権を確認中...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !canAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>アクセス拒否</AlertTitle>
              <AlertDescription className="mt-2">
                {error || 'このページにアクセスする権限がありません。'}
              </AlertDescription>
            </Alert>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setLocation('/select-filter')}
                className="text-sm text-medical-blue hover:underline"
              >
                トップページに戻る
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
